import { useState } from "react";

/**
 * <TeamMembersPicker /> — extracted from JobCardDetail.jsx (Wave 25.16
 * Phase 2 refactor). Multi-pick team members for a JC with drag-to-
 * reorder and the same UX as the Required Skills picker.
 *
 * Props:
 *   value          — array of user ids (preserved order)
 *   users          — full user list (already filtered by visibility)
 *   excludeUserId  — primary technician (excluded from the candidate pool)
 *   canEdit        — bool
 *   onChange(next) — array of user ids
 */
export default function TeamMembersPicker({ value = [], users, excludeUserId, canEdit, onChange }) {
  const candidates = users.filter((u) => u.id !== excludeUserId);
  const labelFor = (id) => candidates.find((u) => u.id === id)?.name || id?.slice(0, 8);
  const userBy = (id) => candidates.find((u) => u.id === id);
  const [dragId, setDragId] = useState(null);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((x) => x !== id));
    else onChange([...value, id]);
  };

  const reorder = (fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const next = value.filter((x) => x !== fromId);
    const idx = toId ? next.indexOf(toId) : next.length;
    next.splice(idx === -1 ? next.length : idx, 0, fromId);
    onChange(next);
  };

  if (!canEdit) {
    if (value.length === 0) return <div className="hv-input mt-1 opacity-70">—</div>;
    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        {value.map((id) => (
          <span
            key={id}
            className="text-xs bg-white/[0.05] border border-white/10 rounded-full px-2 py-0.5"
          >
            {labelFor(id)}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-1">
      {/* Selected — drag-orderable chips (R1 regression fix) */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="job-team-members-selected">
          {value.map((id) => {
            const u = userBy(id);
            return (
              <span
                key={id}
                draggable
                onDragStart={(e) => {
                  setDragId(id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (dragId) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  reorder(dragId, id);
                  setDragId(null);
                }}
                className="text-xs bg-[#1fb6c1]/10 border border-[#1fb6c1]/30 text-[#1fb6c1] rounded-full pl-2 pr-1 py-0.5 inline-flex items-center gap-1 cursor-grab active:cursor-grabbing"
                data-testid={`job-team-member-${id}`}
                title="Drag to reorder"
              >
                <span className="font-mono opacity-50 text-[10px]">⋮⋮</span>
                {u?.name || labelFor(id)}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="opacity-70 hover:opacity-100"
                  aria-label="Remove"
                  data-testid={`job-team-member-remove-${id}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Available pool — click to add (mirrors Required Skills UX, #2) */}
      <div className="flex flex-wrap gap-1.5" data-testid="job-team-members-pool">
        {candidates
          .filter((u) => !value.includes(u.id))
          .map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => toggle(u.id)}
              className="text-xs border border-white/10 hover:border-[#1fb6c1]/50 text-[#a0aabf] hover:text-[#1fb6c1] rounded-full px-2 py-0.5 transition"
              data-testid={`job-team-member-add-${u.id}`}
            >
              + {u.name}{" "}
              <span className="opacity-60 text-[10px]">{u.role}</span>
            </button>
          ))}
        {candidates.filter((u) => !value.includes(u.id)).length === 0 && (
          <div className="text-[10px] text-[#647087] italic">All staff added.</div>
        )}
      </div>
    </div>
  );
}
